import { useRef, useState, useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import StructuredData from '@/components/feature/StructuredData';
import { articles } from '@/mocks/articles';

function getArticleImage(id: number): string {
  if (id === 1) return 'https://readdy.ai/api/search-image?query=Dramatic%20scene%20of%20a%20livestream%20setup%20with%20neon%20ring%20light%20falling%20over%2C%20shattered%20makeup%20products%20on%20a%20dark%20floor%2C%20dim%20moody%20lighting%20with%20pink%20and%20cyan%20accents%2C%20cinematic%20composition%2C%20editorial%20photography%20style%20with%20deep%20shadows%20and%20moody%20atmosphere%2C%20simple%20dark%20background&width=800&height=500&seq=about-article-1&orientation=landscape';
  if (id === 2) return 'https://readdy.ai/api/search-image?query=Luxury%20hotel%20entrance%20at%20night%20with%20paparazzi%20camera%20flash%20effect%2C%20sleek%20black%20car%20pulling%20up%2C%20dramatic%20noir%20lighting%20with%20neon%20city%20reflections%2C%20cinematic%20wide%20shot%2C%20editorial%20photography%20with%20deep%20contrast%20and%20moody%20atmosphere%2C%20dark%20urban%20background&width=800&height=500&seq=about-article-2&orientation=landscape';
  if (id === 5) return 'https://readdy.ai/api/search-image?query=Corporate%20office%20building%20at%20night%20with%20lights%20flickering%20dramatically%2C%20stock%20market%20charts%20showing%20sharp%20decline%20on%20dark%20screens%2C%20emergency%20documents%20scattered%20on%20desk%2C%20noir%20thriller%20atmosphere%20with%20cyan%20and%20red%20accent%20lighting%2C%20cinematic%20composition%2C%20dark%20corporate%20environment&width=800&height=500&seq=about-article-5&orientation=landscape';
  return 'https://readdy.ai/api/search-image?query=Dark%20moody%20news%20photography%20scene%20with%20dramatic%20lighting%2C%20abstract%20composition%20with%20pink%20and%20cyan%20neon%20accents%2C%20editorial%20atmosphere%2C%20clean%20dark%20background%20with%20subtle%20texture&width=800&height=500&seq=about-article-fallback&orientation=landscape';
}

export default function AboutPage() {
  const contactRef = useRef<HTMLDivElement>(null);

  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const honeypot = (formData.get('website_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      form.reset();
      return;
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d95mtr6a5sk3luk4pgh0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed: Record<string, unknown> | null = null;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }
      if (response.ok && parsed && (parsed as Record<string, unknown>).code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = (parsed && (parsed as Record<string, unknown>).meta && ((parsed as Record<string, unknown>).meta as Record<string, unknown>).message) || responseText || '提交失败，请稍后重试';
        setFormError(String(serverMsg));
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '911爆料网',
    alternateName: '911黑料网',
    url: 'https://www.911baoliao.com/',
    description: '全网吃瓜黑料第一站入口',
    sameAs: ['https://weibo.com/911baoliao', 'https://www.toutiao.com/911baoliao'],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@911baoliao.com',
      availableLanguage: 'Chinese',
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: '911爆料网是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '911爆料网是全网吃瓜黑料第一站入口，成立于2023年，致力于为全网吃瓜群众提供最新、最全、最真实的爆料信息。我们覆盖网红翻车、明星八卦、每日大赛、反差视频等多个热门板块。',
        },
      },
      {
        '@type': 'Question',
        name: '911爆料网的内容来源是什么？',
        acceptedAnswer: {
          '@type': 'Answer',
          text: '我们的内容来源包括社区用户爆料、专业编辑团队采编、以及合作媒体授权转载。所有内容均经过三级审核机制确保真实性和可信度。',
        },
      },
    ],
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.911baoliao.com/' },
      { '@type': 'ListItem', position: 2, name: '关于我们', item: 'https://www.911baoliao.com/about' },
    ],
  };

  const featuredArticles = articles.filter((a) => a.featured).slice(0, 3);

  return (
    <>
      <StructuredData data={organizationSchema} />
      <StructuredData data={faqSchema} />
      <StructuredData data={breadcrumbSchema} />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main>
          <section className="relative w-full h-[280px] md:h-[360px] overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20futuristic%20newsroom%20with%20holographic%20displays%20showing%20breaking%20news%20headlines%2C%20dark%20sophisticated%20workspace%20with%20neon%20pink%20and%20cyan%20accent%20lighting%2C%20professional%20editorial%20environment%2C%20cinematic%20wide%20shot%2C%20sleek%20corporate%20atmosphere%20with%20dramatic%20shadows%20and%20atmospheric%20glow%2C%20clean%20dark%20background&width=1800&height=700&seq=about-hero&orientation=landscape"
              alt="关于911爆料网"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/75"></div>
            <div className="relative z-10 w-full h-full max-w-7xl mx-auto px-4 md:px-6 flex flex-col justify-center">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                关于我们
              </h1>
              <p className="text-sm md:text-base text-foreground-300 max-w-xl leading-relaxed">
                全网吃瓜黑料第一站，致力于为每一位吃瓜群众提供最优质的爆料体验
              </p>
            </div>
          </section>

          <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
            <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />
          </div>

          <section className="py-14 md:py-20">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
                <div className="lg:w-2/5">
                  <span className="inline-block px-4 py-1.5 rounded-full border border-primary-500/40 text-primary-400 text-xs font-medium mb-5 whitespace-nowrap">
                    品牌介绍
                  </span>
                  <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-8 leading-tight">
                    全网吃瓜
                    <br />
                    <span className="text-primary-400">黑料第一站</span>
                  </h2>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-6">
                    911爆料网成立于2023年，是国内领先的娱乐八卦资讯聚合平台。我们秉持"快、准、全"的核心理念，致力于为全网吃瓜群众提供最新、最全、最真实的爆料信息。
                  </p>
                  <p className="text-sm md:text-base text-foreground-300 leading-relaxed mb-8">
                    我们的编辑团队由资深娱乐记者和互联网内容从业者组成，建立了业内领先的三级审核机制，确保每一条爆料的真实性和可信度。
                  </p>

                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="text-2xl md:text-3xl font-heading font-bold text-primary-400 mb-1">2.8M+</div>
                      <p className="text-xs text-foreground-400 whitespace-nowrap">注册用户</p>
                    </div>
                    <div>
                      <div className="text-2xl md:text-3xl font-heading font-bold text-accent-400 mb-1">50K+</div>
                      <p className="text-xs text-foreground-400 whitespace-nowrap">每日爆料</p>
                    </div>
                  </div>
                </div>

                <div className="lg:w-3/5 relative">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="relative col-span-2">
                      <div className="aspect-[16/9] rounded-2xl overflow-hidden">
                        <img
                          src="https://readdy.ai/api/search-image?query=Modern%20open-plan%20newsroom%20office%20with%20large%20screens%20displaying%20breaking%20news%20and%20data%20visualizations%2C%20sleek%20dark%20furniture%20with%20neon%20pink%20and%20cyan%20accent%20lighting%2C%20professional%20editorial%20team%20collaborating%2C%20futuristic%20media%20workspace%20atmosphere%2C%20cinematic%20wide%20shot%2C%20sophisticated%20dark%20background&width=800&height=450&seq=about-img-1&orientation=landscape"
                          alt="911爆料网编辑部"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="aspect-square rounded-2xl overflow-hidden">
                        <img
                          src="https://readdy.ai/api/search-image?query=Professional%20editor%20working%20at%20sleek%20dark%20desk%20with%20multiple%20monitors%20showing%20trending%20news%20feeds%20and%20social%20media%20analytics%2C%20modern%20cyberpunk%20aesthetic%20workspace%20with%20pink%20and%20cyan%20ambient%20lighting%2C%20focused%20creative%20atmosphere%2C%20editorial%20documentary%20style%2C%20clean%20dark%20background&width=400&height=400&seq=about-img-2&orientation=squarish"
                          alt="编辑团队"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div>
                      <div className="aspect-square rounded-2xl overflow-hidden">
                        <img
                          src="https://readdy.ai/api/search-image?query=High-tech%20data%20monitoring%20center%20with%20holographic%20screens%20displaying%20real-time%20trending%20topics%20and%20breaking%20news%20alerts%2C%20futuristic%20cyberpunk%20aesthetic%2C%20dramatic%20moody%20lighting%20with%20electric%20pink%20and%20cyan%20neon%20accents%2C%20sophisticated%20surveillance%20atmosphere%2C%20cinematic%20composition%2C%20dark%20background&width=400&height=400&seq=about-img-3&orientation=squarish"
                          alt="实时监控中心"
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 rounded-full border border-accent-500/40 text-accent-400 text-xs font-medium mb-4 whitespace-nowrap">
                  核心优势
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                  为什么选择我们
                </h2>
                <p className="text-sm md:text-base text-foreground-300 max-w-xl mx-auto">
                  四大核心优势，打造全网最好的吃瓜体验
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {[
                  { icon: 'ri-flashlight-line', title: '极速更新', desc: '7x24小时全天候更新，大瓜发生到上线不超30分钟' },
                  { icon: 'ri-shield-check-line', title: '三重审核', desc: 'AI初筛 + 编辑核查 + 主编终审，确保爆料真实可信' },
                  { icon: 'ri-global-line', title: '全网覆盖', desc: '覆盖微博、抖音、小红书等全平台热门话题和最新动态' },
                  { icon: 'ri-heart-line', title: '完全免费', desc: '所有内容永久免费开放，无需付费即可畅享全部功能' },
                ].map((item) => (
                  <div key={item.title} className="bg-background-200 border border-background-300 rounded-2xl p-5 md:p-6 text-center transition-all duration-300 hover:bg-background-300 hover:border-background-400">
                    <div className="w-12 h-12 rounded-xl bg-primary-100/20 flex items-center justify-center mx-auto mb-4">
                      <i className={`${item.icon} text-xl text-primary-400`}></i>
                    </div>
                    <h3 className="text-base font-heading font-bold text-foreground-50 mb-2 whitespace-nowrap">{item.title}</h3>
                    <p className="text-sm text-foreground-300 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-14 md:py-20">
            <div className="w-full max-w-7xl mx-auto px-4 md:px-6">
              <div className="text-center mb-12">
                <span className="inline-block px-4 py-1.5 rounded-full border border-primary-500/40 text-primary-400 text-xs font-medium mb-4 whitespace-nowrap">
                  媒体矩阵
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                  热门报道
                </h2>
                <p className="text-sm md:text-base text-foreground-300 max-w-xl mx-auto">
                  911爆料网持续产出高质量爆料内容，引领吃瓜新潮流
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {featuredArticles.map((article, idx) => (
                  <article key={article.id} className="group bg-background-200 border border-background-300 rounded-2xl overflow-hidden transition-all duration-300 hover:bg-background-300 hover:border-background-400">
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <img
                        src={getArticleImage(article.id)}
                        alt={article.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading={idx < 3 ? 'eager' : 'lazy'}
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-2.5 py-1 rounded-full bg-primary-500 text-foreground-50 text-xs font-medium whitespace-nowrap">
                          热门
                        </span>
                      </div>
                    </div>
                    <div className="p-4 md:p-5">
                      <h3 className="text-base md:text-lg font-heading font-bold text-foreground-50 mb-2 line-clamp-2 group-hover:text-primary-400 transition-colors">
                        {article.title}
                      </h3>
                      <div className="flex items-center justify-between text-xs text-foreground-400">
                        <span className="whitespace-nowrap">{article.date}</span>
                        <span className="whitespace-nowrap">{article.views.toLocaleString()} 阅读</span>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          <section ref={contactRef} className="py-14 md:py-20 bg-background-100">
            <div className="w-full max-w-2xl mx-auto px-4 md:px-6">
              <div className="text-center mb-10">
                <span className="inline-block px-4 py-1.5 rounded-full border border-accent-500/40 text-accent-400 text-xs font-medium mb-4 whitespace-nowrap">
                  联系我们
                </span>
                <h2 className="text-2xl md:text-4xl font-heading font-bold text-foreground-50 mb-3">
                  期待与你交流
                </h2>
                <p className="text-sm md:text-base text-foreground-300">
                  有任何问题、建议或爆料线索，欢迎通过以下方式联系我们
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="bg-background-200 border border-background-300 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-primary-100/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-mail-line text-lg text-primary-400"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-1 whitespace-nowrap">爆料投稿</h3>
                  <p className="text-xs text-foreground-400">tips@911baoliao.com</p>
                </div>
                <div className="bg-background-200 border border-background-300 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-accent-100/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-customer-service-line text-lg text-accent-400"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-1 whitespace-nowrap">商务合作</h3>
                  <p className="text-xs text-foreground-400">biz@911baoliao.com</p>
                </div>
                <div className="bg-background-200 border border-background-300 rounded-xl p-5 text-center">
                  <div className="w-10 h-10 rounded-full bg-secondary-100/20 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-question-answer-line text-lg text-secondary-400"></i>
                  </div>
                  <h3 className="text-sm font-heading font-bold text-foreground-50 mb-1 whitespace-nowrap">用户反馈</h3>
                  <p className="text-xs text-foreground-400">feedback@911baoliao.com</p>
                </div>
              </div>

              <form data-readdy-form onSubmit={handleSubmit} className="bg-background-200 border border-background-300 rounded-2xl p-6 md:p-8">
                {formStatus === 'success' && (
                  <div className="mb-5 p-4 rounded-xl bg-primary-100/20 text-primary-400 text-sm">
                    感谢你的留言！我们会尽快回复。
                  </div>
                )}
                {formStatus === 'error' && (
                  <div className="mb-5 p-4 rounded-xl bg-red-900/20 text-red-400 text-sm">
                    {formError}
                  </div>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    name="name"
                    placeholder="你的名字"
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                    required
                  />
                  <input
                    type="email"
                    name="email"
                    placeholder="你的邮箱"
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors"
                    required
                  />
                </div>
                <div className="mb-4">
                  <select
                    name="subject"
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-50 focus:outline-none focus:border-primary-400 transition-colors appearance-none"
                    defaultValue=""
                    required
                  >
                    <option value="" disabled>请选择主题</option>
                    <option value="爆料投稿">爆料投稿</option>
                    <option value="商务合作">商务合作</option>
                    <option value="内容建议">内容建议</option>
                    <option value="技术问题">技术问题</option>
                    <option value="其他">其他</option>
                  </select>
                </div>
                <div className="mb-5">
                  <textarea
                    name="message"
                    placeholder="请输入你的留言内容（最多500字）"
                    className="w-full px-4 py-3 rounded-xl bg-background-50 border border-background-300 text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none focus:border-primary-400 transition-colors resize-none"
                    rows={5}
                    maxLength={500}
                    required
                  ></textarea>
                </div>
                <input
                  type="text"
                  name="website_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                  className="honeypot-field"
                />
                <button
                  type="submit"
                  className="w-full py-3 rounded-xl bg-primary-500 text-foreground-50 font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                >
                  发送留言
                </button>
              </form>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}